import { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import PageSEO from '@/components/PageSEO';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Input } from '@/components/ui/input';
import { FlaskConical, ArrowLeftRight, X, Search, ChevronRight, Menu, User } from 'lucide-react';
import { api } from '@/api/api';
import { toast } from 'sonner';
import { Footer } from '@/components/Footer';
import { ThemeToggle } from '@/components/ThemeToggle';

const ScoreBar = ({ label, valueA, valueB, max, colorA = 'bg-blue-500', colorB = 'bg-emerald-500' }) => (
  <div className="space-y-2" data-testid={`score-bar-${label.toLowerCase().replace(/\s/g, '-')}`}>
    <div className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">{label}</div>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
      <div>
        <div className="flex justify-between text-sm mb-1">
          <span className="font-bold text-slate-800 dark:text-slate-200 flex items-center gap-1.5">
            <span className="inline sm:hidden w-2 h-2 rounded-full bg-blue-500 flex-shrink-0" />
            {valueA ?? '—'}
          </span>
          <span className="text-slate-400">/{max}</span>
        </div>
        <div className="h-2.5 bg-slate-100 dark:bg-slate-700 rounded-full overflow-hidden">
          <div className={`h-full ${colorA} rounded-full transition-all duration-700`} style={{ width: `${((valueA ?? 0) / max) * 100}%` }} />
        </div>
      </div>
      <div>
        <div className="flex justify-between text-sm mb-1">
          <span className="font-bold text-slate-800 dark:text-slate-200 flex items-center gap-1.5">
            <span className="inline sm:hidden w-2 h-2 rounded-full bg-emerald-500 flex-shrink-0" />
            {valueB ?? '—'}
          </span>
          <span className="text-slate-400">/{max}</span>
        </div>
        <div className="h-2.5 bg-slate-100 dark:bg-slate-700 rounded-full overflow-hidden">
          <div className={`h-full ${colorB} rounded-full transition-all duration-700`} style={{ width: `${((valueB ?? 0) / max) * 100}%` }} />
        </div>
      </div>
    </div>
  </div>
);

const MetaRow = ({ label, valA, valB }) => (
  <div className="flex flex-col sm:grid sm:grid-cols-[100px_1fr_1fr] gap-1 sm:gap-3 py-2.5 border-b border-slate-100 dark:border-slate-700 last:border-0">
    <div className="text-xs font-semibold text-slate-400 dark:text-slate-500 uppercase tracking-wider self-start pt-0.5">{label}</div>
    <div className="text-sm text-slate-700 dark:text-slate-300 pl-3 sm:pl-0">{valA || '—'}</div>
    <div className="text-sm text-slate-700 dark:text-slate-300 pl-3 sm:pl-0">{valB || '—'}</div>
  </div>
);

const ArticleSelector = ({ articles, selectedId, onSelect, label, searchQuery, onSearchChange }) => (
  <div className="space-y-3" data-testid={`article-selector-${label}`}>
    <div className="relative">
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
      <Input
        placeholder="Search articles..."
        value={searchQuery}
        onChange={(e) => onSearchChange(e.target.value)}
        className="pl-9 bg-white dark:bg-slate-800"
        data-testid={`search-${label}`}
      />
    </div>
    <div className="max-h-64 overflow-y-auto space-y-1.5 pr-1">
      {articles.map((a) => (
        <button
          key={a.id}
          onClick={() => onSelect(a.id)}
          className={`w-full text-left p-3 rounded-lg border transition-all text-sm ${
            selectedId === a.id
              ? 'border-blue-400 bg-blue-50 dark:bg-blue-950/30 dark:border-blue-600'
              : 'border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600 bg-white dark:bg-slate-800'
          }`}
          data-testid={`select-article-${a.id}`}
        >
          <div className="font-medium text-slate-800 dark:text-slate-200 line-clamp-2 leading-snug">{a.title}</div>
          <div className="flex items-center gap-2 mt-1.5">
            {a.normalized_study_type && <Badge variant="outline" className="text-[10px] py-0">{a.normalized_study_type}</Badge>}
            {a.impact_index != null && <span className="text-xs text-blue-600 dark:text-blue-400 font-bold">Impact: {a.impact_index}</span>}
          </div>
        </button>
      ))}
      {articles.length === 0 && (
        <p className="text-sm text-slate-400 text-center py-4">No articles found</p>
      )}
    </div>
  </div>
);

const CompareArticles = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [allArticles, setAllArticles] = useState([]);
  const [compareData, setCompareData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selecting, setSelecting] = useState(true);
  const [selectedA, setSelectedA] = useState(searchParams.get('a') || '');
  const [selectedB, setSelectedB] = useState(searchParams.get('b') || '');
  const [searchA, setSearchA] = useState('');
  const [searchB, setSearchB] = useState('');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isAuthorLoggedIn, setIsAuthorLoggedIn] = useState(false);

  useEffect(() => {
    const loadArticles = async () => {
      try {
        const data = await api.exploreArticles({ limit: 100 });
        setAllArticles(data);
      } catch { toast.error('Failed to load articles'); }
    };
    loadArticles();
    setIsAuthorLoggedIn(!!sessionStorage.getItem('author_authenticated'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const doCompare = useCallback(async (idA, idB) => {
    if (!idA || !idB) return;
    if (idA === idB) { toast.error('Please select two different articles'); return; }
    setLoading(true);
    try {
      const data = await api.compareArticles([idA, idB]);
      setCompareData(data.articles);
      setSelecting(false);
      setSearchParams({ a: idA, b: idB });
    } catch { toast.error('Failed to compare articles'); }
    setLoading(false);
  }, [setSearchParams]);

  // Auto-compare if URL params present
  useEffect(() => {
    const a = searchParams.get('a');
    const b = searchParams.get('b');
    if (a && b && allArticles.length > 0 && !compareData) {
      setSelectedA(a);
      setSelectedB(b);
      doCompare(a, b);
    }
  }, [searchParams, allArticles, compareData, doCompare]);

  const filteredA = allArticles.filter(a => {
    if (!searchA.trim()) return true;
    const q = searchA.toLowerCase();
    return a.title?.toLowerCase().includes(q) || a.journal?.toLowerCase().includes(q);
  });

  const filteredB = allArticles.filter(a => {
    if (!searchB.trim()) return true;
    const q = searchB.toLowerCase();
    return a.title?.toLowerCase().includes(q) || a.journal?.toLowerCase().includes(q);
  });

  const a = compareData?.[0];
  const b = compareData?.[1];

  return (
    <>
      <PageSEO
        title="Compare Articles | Evidentica"
        description="Compare two medical research articles side-by-side. Analyze impact scores, methodology, and key findings."
      />
      <div className="min-h-screen bg-slate-50 dark:bg-slate-950">
        {/* Nav */}
        <nav className="bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between h-16 items-center">
              <Link to="/" className="flex items-center space-x-2">
                <FlaskConical className="h-8 w-8 text-blue-600" />
                <span className="text-xl font-bold text-slate-900 dark:text-white">Evidentica</span>
              </Link>
              <div className="hidden md:flex items-center space-x-3">
                <Link to="/explorer"><Button variant="ghost" data-testid="nav-explorer-btn">Explorer</Button></Link>
                <Link to="/top-impact"><Button variant="ghost">Top Impact</Button></Link>
                {isAuthorLoggedIn ? (
                  <Link to="/author/dashboard"><Button variant="ghost"><User className="mr-2 h-4 w-4" />Dashboard</Button></Link>
                ) : (
                  <Link to="/author/login"><Button variant="ghost">Login</Button></Link>
                )}
                <ThemeToggle />
              </div>
              <div className="md:hidden flex items-center gap-1">
                <ThemeToggle />
                <button className="p-2" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
                  {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
                </button>
              </div>
            </div>
            {mobileMenuOpen && (
              <div className="md:hidden py-4 space-y-2">
                <Link to="/explorer" onClick={() => setMobileMenuOpen(false)}><Button variant="ghost" className="w-full justify-start">Explorer</Button></Link>
                <Link to="/top-impact" onClick={() => setMobileMenuOpen(false)}><Button variant="ghost" className="w-full justify-start">Top Impact</Button></Link>
              </div>
            )}
          </div>
        </nav>

        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 dark:text-white flex items-center gap-3" data-testid="compare-title">
                <ArrowLeftRight className="h-8 w-8 text-blue-600" />
                Compare Articles
              </h1>
              <p className="text-slate-500 dark:text-slate-400 mt-1 text-sm">Side-by-side analysis of methodology, impact, and key findings</p>
            </div>
            {!selecting && (
              <Button variant="outline" onClick={() => { setSelecting(true); setCompareData(null); }} data-testid="new-comparison-btn">
                New Comparison
              </Button>
            )}
          </div>

          {/* Selection Mode */}
          {selecting && (
            <Card className="mb-8" data-testid="selection-card">
              <CardContent className="pt-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900/40 flex items-center justify-center text-blue-600 font-bold text-sm">A</div>
                      <span className="font-semibold text-slate-700 dark:text-slate-300">Article A</span>
                      {selectedA && <Badge variant="secondary" className="text-xs">{allArticles.find(x => x.id === selectedA)?.title?.slice(0, 30)}...</Badge>}
                    </div>
                    <ArticleSelector articles={filteredA} selectedId={selectedA} onSelect={setSelectedA} label="a" searchQuery={searchA} onSearchChange={setSearchA} />
                  </div>
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-8 h-8 rounded-full bg-emerald-100 dark:bg-emerald-900/40 flex items-center justify-center text-emerald-600 font-bold text-sm">B</div>
                      <span className="font-semibold text-slate-700 dark:text-slate-300">Article B</span>
                      {selectedB && <Badge variant="secondary" className="text-xs">{allArticles.find(x => x.id === selectedB)?.title?.slice(0, 30)}...</Badge>}
                    </div>
                    <ArticleSelector articles={filteredB} selectedId={selectedB} onSelect={setSelectedB} label="b" searchQuery={searchB} onSearchChange={setSearchB} />
                  </div>
                </div>
                <div className="flex justify-center mt-6">
                  <Button
                    onClick={() => doCompare(selectedA, selectedB)}
                    disabled={!selectedA || !selectedB || loading}
                    className="px-8"
                    data-testid="compare-btn"
                  >
                    {loading ? 'Comparing...' : 'Compare Selected Articles'}
                    <ChevronRight className="ml-2 h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Comparison Results */}
          {!selecting && compareData && a && b && (
            <div className="space-y-6" data-testid="comparison-results">
              {/* Article Titles Header */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Card className="border-t-4 border-t-blue-500">
                  <CardContent className="pt-5 pb-4">
                    <Link to={`/article/${a.id}`} className="hover:text-blue-600 transition-colors">
                      <h2 className="font-bold text-lg text-slate-900 dark:text-white leading-snug" data-testid="article-a-title">{a.title}</h2>
                    </Link>
                    <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">{a.journal}{a.publication_year ? ` (${a.publication_year})` : ''}</p>
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {a.normalized_study_type && <Badge variant="outline" className="text-xs">{a.normalized_study_type}</Badge>}
                      {a.evidence_level && <Badge className="bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300 text-xs">{a.evidence_level}</Badge>}
                    </div>
                  </CardContent>
                </Card>
                <Card className="border-t-4 border-t-emerald-500">
                  <CardContent className="pt-5 pb-4">
                    <Link to={`/article/${b.id}`} className="hover:text-emerald-600 transition-colors">
                      <h2 className="font-bold text-lg text-slate-900 dark:text-white leading-snug" data-testid="article-b-title">{b.title}</h2>
                    </Link>
                    <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">{b.journal}{b.publication_year ? ` (${b.publication_year})` : ''}</p>
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {b.normalized_study_type && <Badge variant="outline" className="text-xs">{b.normalized_study_type}</Badge>}
                      {b.evidence_level && <Badge className="bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300 text-xs">{b.evidence_level}</Badge>}
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Score Comparison */}
              <Card data-testid="score-comparison">
                <CardContent className="pt-6 space-y-5">
                  <h3 className="font-bold text-slate-800 dark:text-white text-sm uppercase tracking-wider">Score Comparison</h3>
                  <ScoreBar label="Impact Index" valueA={a.impact_index} valueB={b.impact_index} max={100} />
                  <ScoreBar label="Methodology Score" valueA={a.methodology_score} valueB={b.methodology_score} max={70} colorA="bg-indigo-500" colorB="bg-teal-500" />
                  <ScoreBar label="Visibility Score" valueA={a.visibility_score} valueB={b.visibility_score} max={30} colorA="bg-amber-500" colorB="bg-orange-500" />
                </CardContent>
              </Card>

              {/* Metadata Comparison */}
              <Card data-testid="metadata-comparison">
                <CardContent className="pt-6">
                  <h3 className="font-bold text-slate-800 dark:text-white text-sm uppercase tracking-wider mb-4">Study Details</h3>
                  <MetaRow label="Authors" valA={(a.authors || []).join(', ')} valB={(b.authors || []).join(', ')} />
                  <MetaRow label="Sample Size" valA={a.sample_size} valB={b.sample_size} />
                  <MetaRow label="Follow-up" valA={a.follow_up_duration_months ? `${a.follow_up_duration_months} months` : null} valB={b.follow_up_duration_months ? `${b.follow_up_duration_months} months` : null} />
                  <MetaRow label="DOI" valA={a.doi ? <a href={`https://doi.org/${a.doi}`} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline text-xs break-all">{a.doi}</a> : null} valB={b.doi ? <a href={`https://doi.org/${b.doi}`} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline text-xs break-all">{b.doi}</a> : null} />
                  <MetaRow label="Evidence" valA={a.evidence_contribution} valB={b.evidence_contribution} />
                </CardContent>
              </Card>

              {/* Key Findings */}
              {(a.key_takeaway || b.key_takeaway) && (
                <Card data-testid="key-findings">
                  <CardContent className="pt-6">
                    <h3 className="font-bold text-slate-800 dark:text-white text-sm uppercase tracking-wider mb-4">Key Takeaways</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="p-4 bg-blue-50 dark:bg-blue-950/20 rounded-lg border border-blue-100 dark:border-blue-900">
                        <div className="text-xs font-semibold text-blue-600 dark:text-blue-400 mb-1 md:hidden">Article A</div>
                        <p className="text-sm text-slate-700 dark:text-slate-300 leading-relaxed">{a.key_takeaway || 'No AI analysis available'}</p>
                      </div>
                      <div className="p-4 bg-emerald-50 dark:bg-emerald-950/20 rounded-lg border border-emerald-100 dark:border-emerald-900">
                        <div className="text-xs font-semibold text-emerald-600 dark:text-emerald-400 mb-1 md:hidden">Article B</div>
                        <p className="text-sm text-slate-700 dark:text-slate-300 leading-relaxed">{b.key_takeaway || 'No AI analysis available'}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Research Insight */}
              {(a.key_research_insight || b.key_research_insight) && (
                <Card data-testid="research-insight">
                  <CardContent className="pt-6">
                    <h3 className="font-bold text-slate-800 dark:text-white text-sm uppercase tracking-wider mb-4">Research Insight</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
                        <div className="text-xs font-semibold text-blue-600 dark:text-blue-400 mb-1 md:hidden">Article A</div>
                        <p className="text-sm text-slate-700 dark:text-slate-300 leading-relaxed">{a.key_research_insight || '—'}</p>
                      </div>
                      <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
                        <div className="text-xs font-semibold text-emerald-600 dark:text-emerald-400 mb-1 md:hidden">Article B</div>
                        <p className="text-sm text-slate-700 dark:text-slate-300 leading-relaxed">{b.key_research_insight || '—'}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Strengths & Limitations */}
              {(a.methodological_strengths?.length > 0 || b.methodological_strengths?.length > 0) && (
                <Card data-testid="strengths-limitations">
                  <CardContent className="pt-6">
                    <h3 className="font-bold text-slate-800 dark:text-white text-sm uppercase tracking-wider mb-4">Strengths & Limitations</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {[a, b].map((art, i) => (
                        <div key={art?.id || `compare-${i}`} className="space-y-3">
                          <div className="text-xs font-semibold text-slate-500 dark:text-slate-400 md:hidden">{i === 0 ? 'Article A' : 'Article B'}</div>
                          {art.methodological_strengths?.length > 0 && (
                            <div>
                              <div className="text-xs font-semibold text-green-600 dark:text-green-400 uppercase mb-1.5">Strengths</div>
                              <ul className="space-y-1">
                                {art.methodological_strengths.map((s, j) => (
                                  <li key={j} className="text-sm text-slate-600 dark:text-slate-400 flex items-start gap-1.5">
                                    <span className="text-green-500 mt-0.5 flex-shrink-0">+</span>{s}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                          {art.methodological_limitations?.length > 0 && (
                            <div>
                              <div className="text-xs font-semibold text-red-500 dark:text-red-400 uppercase mb-1.5">Limitations</div>
                              <ul className="space-y-1">
                                {art.methodological_limitations.map((l, j) => (
                                  <li key={j} className="text-sm text-slate-600 dark:text-slate-400 flex items-start gap-1.5">
                                    <span className="text-red-400 mt-0.5 flex-shrink-0">-</span>{l}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                          {!art.methodological_strengths?.length && !art.methodological_limitations?.length && (
                            <p className="text-sm text-slate-400">No analysis data</p>
                          )}
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* PICO Comparison */}
              {(a.pico || b.pico) && (
                <Card data-testid="pico-comparison">
                  <CardContent className="pt-6">
                    <h3 className="font-bold text-slate-800 dark:text-white text-sm uppercase tracking-wider mb-4">PICO Framework</h3>
                    {['population', 'intervention', 'comparator', 'outcome'].map((field) => (
                      <MetaRow
                        key={field}
                        label={field.charAt(0).toUpperCase() + field.slice(1)}
                        valA={a.pico?.[field]}
                        valB={b.pico?.[field]}
                      />
                    ))}
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </div>
        <Footer />
      </div>
    </>
  );
};

export default CompareArticles;
